/**
 * HandInteractionController — Traduce el estado de las manos en interacciones
 * con el grafo 3D.
 *
 * Diseño clave: usamos RAYCAST 2D desde la posición pantalla del dedo índice
 * (state.leftHand/rightHand.indexTipScreen) igual que el MouseController.
 * MediaPipe no entrega Z confiable, así que la distancia 3D daba falsos
 * negativos (el dedo parecía "atrás" del nodo). Con raycast 2D hit-test es
 * idéntico al click del mouse.
 *
 * Gestos:
 *   - Pinch corto sobre nodo  → click: selecciona + expand (icono + pulso)
 *   - Pinch sostenido > 200ms → agranda el nodo proporcional al tiempo
 *   - Puño cerrado sobre nodo → arrastra el nodo en un plano paralelo a la cámara
 *   - Dos manos separándose   → zoom out (dolly cámara hacia atrás)
 *   - Dos manos juntándose    → zoom in (dolly cámara hacia adelante)
 *   - Mano abierta quieta     → empuja nodos cercanos (repulsión suave)
 */

import * as THREE from 'three';
import state, { HandState } from '../core/state';
import { Patient } from '../medical/Patient';
import { ClinicalNode } from '../medical/ClinicalNode';
import { MedicalPhysics } from '../physics/MedicalPhysics';
import { SceneManager } from '../visualization/SceneManager';

export class HandInteractionController {
  private grabbedNodeId: string | null = null;
  private grabOffsetWorld = new THREE.Vector3();
  private dragPlane = new THREE.Plane();

  private pinchNodeId: string | null = null;
  private pinchStart = 0;
  private pinchDidClick = false;

  private prevTwoHandDist = 0;

  // Reusables para evitar allocations cada frame
  private raycaster = new THREE.Raycaster();
  private ndc = new THREE.Vector2();

  constructor(private sceneManager: SceneManager) {}

  private patient: Patient | null = null;
  private physics: MedicalPhysics | null = null;

  setPatient(patient: Patient | null): void {
    this.patient = patient;
    this.grabbedNodeId = null;
    this.pinchNodeId = null;
    this.pinchStart = 0;
    this.pinchDidClick = false;
  }

  setPhysics(physics: MedicalPhysics | null): void {
    this.physics = physics;
  }

  /** Llamar cada frame desde el loop principal. */
  update(): void {
    if (!this.patient) return;

    const rh = state.rightHand;
    const lh = state.leftHand;

    if (rh.isPresent && lh.isPresent && rh.indexTipScreen && lh.indexTipScreen) {
      this.handleTwoHandsZoom(rh.indexTipScreen, lh.indexTipScreen);
      return;
    }
    this.prevTwoHandDist = 0;

    const hand = state.getActiveHand();
    if (!hand?.isPresent || !hand.indexTipScreen) {
      this.resetGestureState();
      return;
    }

    // Hit test 2D desde la posición del índice (consistente con mouse).
    const hitNode = this.raycastFromScreen(hand.indexTipScreen);
    if (hitNode) state.setHoveredNode(hitNode.data.id);

    this.handleGrab(hand, hitNode);
    this.handlePinch(hand, hitNode);

    // Repulsión suave con mano abierta (empujar nodos con la palma).
    if (!hand.isPinching && !hand.isGrabbing && this.physics) {
      this.physics.applyMouseRepulsion(hand.position, 1.0);
    }
  }

  private resetGestureState(): void {
    if (this.grabbedNodeId) {
      state.setDraggedNode(null);
      this.grabbedNodeId = null;
    }
    if (this.pinchNodeId && this.patient) {
      const node = this.patient.getNode(this.pinchNodeId);
      if (node) this.restoreScale(node);
    }
    this.pinchStart = 0;
    this.pinchNodeId = null;
    this.pinchDidClick = false;
  }

  /**
   * Raycast 2D idéntico al del MouseController. La posición viene ya flipeada
   * (indexTipScreen guarda 1-x para compensar el espejo de la cámara frontal).
   */
  private raycastFromScreen(screen: { x: number; y: number }): ClinicalNode | null {
    if (!this.patient) return null;
    // Convertir 0..1 → NDC -1..1. Y invertido porque NDC tiene Y hacia arriba.
    this.ndc.set(screen.x * 2 - 1, -(screen.y * 2 - 1));
    this.raycaster.setFromCamera(this.ndc, this.sceneManager.camera);

    const meshes: THREE.Object3D[] = [];
    const nodeByMesh = new Map<THREE.Object3D, ClinicalNode>();
    this.patient.nodes.forEach(n => {
      if (n.visible) {
        meshes.push(n.mesh);
        nodeByMesh.set(n.mesh, n);
      }
    });
    const hits = this.raycaster.intersectObjects(meshes, false);
    if (hits.length === 0) return null;
    return nodeByMesh.get(hits[0].object) ?? null;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Dos manos: zoom de cámara
  // ─────────────────────────────────────────────────────────────────────

  private handleTwoHandsZoom(
    rTip: { x: number; y: number },
    lTip: { x: number; y: number }
  ): void {
    const dist = Math.hypot(rTip.x - lTip.x, rTip.y - lTip.y);

    if (this.prevTwoHandDist > 0) {
      const dd = dist - this.prevTwoHandDist;
      if (Math.abs(dd) > 0.003) {
        // Separar manos (dd > 0) = zoom out (alejar cámara del target).
        // Juntar manos (dd < 0) = zoom in (acercar).
        const controls = this.sceneManager.controls;
        const cam = this.sceneManager.camera;
        const target = controls.target;
        const dir = cam.position.clone().sub(target);
        const currentDist = dir.length();
        const newDist = THREE.MathUtils.clamp(
          currentDist * (1 + dd * 2.5),
          3,
          60
        );
        dir.setLength(newDist);
        cam.position.copy(target).add(dir);
      }
    }
    this.prevTwoHandDist = dist;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Grab (puño cerrado): arrastra el nodo
  // ─────────────────────────────────────────────────────────────────────

  private handleGrab(hand: HandState, hitNode: ClinicalNode | null): void {
    if (!this.patient) return;

    if (hand.isGrabbing) {
      if (!this.grabbedNodeId && hitNode) {
        this.grabbedNodeId = hitNode.data.id;
        state.setDraggedNode(hitNode.data.id);
        // Plano de drag paralelo a la cámara, pasando por el nodo
        const camDir = this.sceneManager.camera.getWorldDirection(new THREE.Vector3());
        this.dragPlane.setFromNormalAndCoplanarPoint(camDir, hitNode.position);
        this.grabOffsetWorld.set(0, 0, 0); // no offset, el cursor se pega al nodo
        hitNode.pulse();
      }
      if (this.grabbedNodeId && hand.indexTipScreen) {
        const node = this.patient.getNode(this.grabbedNodeId);
        if (node) {
          this.ndc.set(hand.indexTipScreen.x * 2 - 1, -(hand.indexTipScreen.y * 2 - 1));
          this.raycaster.setFromCamera(this.ndc, this.sceneManager.camera);
          const target = new THREE.Vector3();
          if (this.raycaster.ray.intersectPlane(this.dragPlane, target)) {
            target.add(this.grabOffsetWorld);
            node.position.lerp(target, 0.45);
            node.velocity.set(0, 0, 0);
          }
        }
      }
    } else if (this.grabbedNodeId) {
      state.setDraggedNode(null);
      this.grabbedNodeId = null;
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Pinch: click corto = select; sostenido = escala
  // ─────────────────────────────────────────────────────────────────────

  private handlePinch(hand: HandState, hitNode: ClinicalNode | null): void {
    if (!this.patient) return;

    if (hand.isPinching) {
      if (this.pinchStart === 0) {
        this.pinchStart = Date.now();
        this.pinchDidClick = false;
        if (hitNode) {
          // Click inmediato al iniciar pinch sobre un nodo: sin latencia y
          // equivalente al click del mouse. expand + pulse = feedback visual.
          this.pinchNodeId = hitNode.data.id;
          state.setSelectedNode(hitNode.data.id);
          hitNode.expand();
          hitNode.pulse();
          this.pinchDidClick = true;
          console.log('[HandInteraction] ✅ click por pinch en', hitNode.data.concept.display);
        }
      }

      const held = Date.now() - this.pinchStart;

      if (this.pinchNodeId && held > 250) {
        // Pinch sostenido: agrandar el nodo proporcional al tiempo (hasta 4×).
        const node = this.patient.getNode(this.pinchNodeId);
        if (node) {
          const s = Math.min(4.0, 1.0 + (held - 250) / 600);
          node.mesh.scale.setScalar(s);
          const baseGlow = node.getBoundingRadius() * 3;
          node.glow.scale.setScalar(baseGlow * s);
        }
      }
    } else if (this.pinchStart > 0) {
      if (this.pinchNodeId) {
        const node = this.patient.getNode(this.pinchNodeId);
        if (node) this.restoreScale(node);
      }
      this.pinchStart = 0;
      this.pinchNodeId = null;
      this.pinchDidClick = false;
    }
  }

  /**
   * Al soltar un pinch sostenido, rampa el mesh de vuelta a scale 1 y
   * el glow a su tamaño base.
   */
  private restoreScale(node: ClinicalNode): void {
    const baseGlow = node.getBoundingRadius() * 3;
    const step = () => {
      if (node.mesh.scale.x > 1.05) {
        node.mesh.scale.multiplyScalar(0.85);
        node.glow.scale.multiplyScalar(0.85);
        requestAnimationFrame(step);
      } else {
        node.mesh.scale.setScalar(1);
        node.glow.scale.setScalar(baseGlow);
      }
    };
    step();
  }
}

export default HandInteractionController;
